import { BlurFilter, Container, Rectangle, Sprite, type Texture } from 'pixi.js';
import type { Disposable } from '../utils/Disposable.js';
import type { ReelSymbol } from '../symbols/ReelSymbol.js';

/**
 * The slice of a PixiJS renderer the cache needs. `Renderer` (WebGL or
 * WebGPU) satisfies it structurally; tests can pass a stub.
 */
export interface SnapshotRenderer {
  generateTexture(options: {
    target: Container;
    frame?: Rectangle;
    resolution?: number;
    antialias?: boolean;
  }): Texture;
}

/** Tuning for the baked motion-blur variant of a snapshot. */
export interface MotionBlurOptions {
  /**
   * Smear axis — the reel's travel direction. `StaticSpinSymbol` defaults
   * this to the owning set's orientation (`'y'` vertical, `'x'` horizontal),
   * so it is only worth setting to override that, or when calling the cache
   * directly. The other axis is never blurred.
   */
  axis?: 'y' | 'x';
  /**
   * Blur strength in pixels along the axis. Default: 20% of the cell's
   * span on that axis (`cellHeight * 0.2` for `'y'`, `cellWidth * 0.2`
   * for `'x'`).
   */
  strength?: number;
  /** BlurFilter quality (number of passes). Default: 4. */
  quality?: number;
  /**
   * Extra transparent pixels added on both sides of the cell along the
   * axis so the smear isn't clipped at the texture edge.
   * Default: `ceil(strength)`.
   */
  padding?: number;
}

export interface SpinTextureCacheOptions {
  /** The renderer used to generate snapshot textures (`app.renderer`). */
  renderer: SnapshotRenderer;
  /** Resolution for generated textures. Default: the renderer's default. */
  resolution?: number;
  /** Default motion-blur tuning for `captureBlurred`. */
  blur?: MotionBlurOptions;
}

interface CacheEntry {
  texture: Texture;
  /** true = generated by this cache (destroyed on invalidate), false = user-provided. */
  owned: boolean;
  width: number;
  height: number;
  /** Smear axis the blurred variant was baked for (blurred entries only). */
  axis?: 'y' | 'x';
}

/**
 * Per-symbolId cache of "spin textures". flat snapshots a reel shows
 * instead of the live symbol (Spine skeleton, animated sprite, ...) while
 * it spins.
 *
 * Two flavors per symbolId:
 *
 *   - **static**. the symbol rendered once into a RenderTexture at cell size.
 *   - **blurred**. the static snapshot smeared vertically through a one-time
 *     BlurFilter pass into a taller, padded RenderTexture. At spin time it's
 *     an ordinary sprite texture: zero filters per frame.
 *
 * Textures come from two sources, user-provided always winning:
 *
 *   - `setStatic(id, tex)` / `setBlurred(id, tex)`. hand-authored art from
 *     your atlas. Never destroyed by the cache.
 *   - `captureStatic` / `captureBlurred`. generated on demand from a live
 *     symbol view. Owned by the cache and destroyed on `invalidate` /
 *     `clear` / `destroy`.
 *
 * Captures are keyed by symbolId and remembered together with the cell size
 * they were taken at; capturing again at a different size regenerates.
 * Share ONE cache across all reels/symbols of a reel set. that's the point.
 */
export class SpinTextureCache implements Disposable {
  private _renderer: SnapshotRenderer;
  private _resolution: number | undefined;
  private _blurDefaults: MotionBlurOptions;
  private _static = new Map<string, CacheEntry>();
  private _blurred = new Map<string, CacheEntry>();
  private _isDestroyed = false;

  constructor(options: SpinTextureCacheOptions) {
    this._renderer = options.renderer;
    this._resolution = options.resolution;
    this._blurDefaults = options.blur ?? {};
  }

  // -- User-provided textures ----------------------------------------------

  /** Provide a hand-authored static spin texture. Wins over captures. */
  setStatic(symbolId: string, texture: Texture): void {
    this._put(this._static, symbolId, { texture, owned: false, width: 0, height: 0 });
  }

  /** Provide a hand-authored motion-blur texture. Wins over captures. */
  setBlurred(symbolId: string, texture: Texture): void {
    this._put(this._blurred, symbolId, { texture, owned: false, width: 0, height: 0 });
  }

  // -- Lookups ---------------------------------------------------------------

  getStatic(symbolId: string): Texture | null {
    return this._static.get(symbolId)?.texture ?? null;
  }

  getBlurred(symbolId: string): Texture | null {
    return this._blurred.get(symbolId)?.texture ?? null;
  }

  hasStatic(symbolId: string): boolean {
    return this._static.has(symbolId);
  }

  hasBlurred(symbolId: string): boolean {
    return this._blurred.has(symbolId);
  }

  // -- Capture ---------------------------------------------------------------

  /**
   * Snapshot `source` (a symbol's `view`, already activated and resized to
   * the cell) into a `width`x`height` texture and cache it under `symbolId`.
   * Returns the cached texture if one already exists at this size; a
   * user-provided texture always short-circuits.
   */
  captureStatic(symbolId: string, source: Container, width: number, height: number): Texture {
    const existing = this._static.get(symbolId);
    if (existing && (!existing.owned || (existing.width === width && existing.height === height))) {
      return existing.texture;
    }
    const texture = this._renderer.generateTexture({
      target: source,
      frame: new Rectangle(0, 0, width, height),
      resolution: this._resolution,
      antialias: true,
    });
    this._put(this._static, symbolId, { texture, owned: true, width, height });
    return texture;
  }

  /**
   * Bake a motion-blurred variant of the static snapshot for `symbolId`
   * (which must exist. call `captureStatic` or `setStatic` first) and
   * cache it. The smear runs along `blur.axis` — the reel's travel
   * direction (`'y'` default when called directly; `StaticSpinSymbol`
   * derives it from the set's orientation) — and the
   * result is `2 * padding` larger than the cell on that axis only. Draw
   * it center-anchored at the cell center and the smear extends evenly
   * past the cell on both sides.
   */
  captureBlurred(
    symbolId: string,
    width: number,
    height: number,
    blur?: MotionBlurOptions,
  ): Texture {
    const axis = blur?.axis ?? this._blurDefaults.axis ?? 'y';
    const existing = this._blurred.get(symbolId);
    if (
      existing &&
      (!existing.owned ||
        (existing.width === width && existing.height === height && existing.axis === axis))
    ) {
      return existing.texture;
    }
    const staticTex = this.getStatic(symbolId);
    if (!staticTex) {
      throw new Error(
        `SpinTextureCache.captureBlurred('${symbolId}'): no static texture to blur. ` +
          `Call captureStatic() or setStatic() for this symbolId first.`,
      );
    }

    const strength =
      blur?.strength ?? this._blurDefaults.strength ?? (axis === 'y' ? height : width) * 0.2;
    const quality = blur?.quality ?? this._blurDefaults.quality ?? 4;
    const padding = blur?.padding ?? this._blurDefaults.padding ?? Math.ceil(strength);

    const wrap = new Container();
    const sprite = new Sprite(staticTex);
    // Fit the source into the cell box in case it's a user-provided static
    // texture with different dimensions.
    sprite.width = width;
    sprite.height = height;
    if (axis === 'y') {
      sprite.y = padding;
      sprite.filters = [new BlurFilter({ strengthX: 0, strengthY: strength, quality })];
    } else {
      sprite.x = padding;
      sprite.filters = [new BlurFilter({ strengthX: strength, strengthY: 0, quality })];
    }
    wrap.addChild(sprite);

    const texture = this._renderer.generateTexture({
      target: wrap,
      frame: new Rectangle(
        0,
        0,
        width + (axis === 'x' ? padding * 2 : 0),
        height + (axis === 'y' ? padding * 2 : 0),
      ),
      resolution: this._resolution,
      antialias: true,
    });
    // Destroy the scratch scene but not the cached static texture.
    wrap.destroy({ children: true });

    this._put(this._blurred, symbolId, { texture, owned: true, width, height, axis });
    return texture;
  }

  // -- Invalidation ------------------------------------------------------------

  /** Drop (and destroy, if cache-generated) both textures for one symbolId. */
  invalidate(symbolId: string): void {
    this._drop(this._static, symbolId);
    this._drop(this._blurred, symbolId);
  }

  /** Drop everything. Call when the cell size changes (responsive relayout). */
  clear(): void {
    for (const id of [...this._static.keys()]) this._drop(this._static, id);
    for (const id of [...this._blurred.keys()]) this._drop(this._blurred, id);
  }

  destroy(): void {
    if (this._isDestroyed) return;
    this.clear();
    this._isDestroyed = true;
  }

  get isDestroyed(): boolean {
    return this._isDestroyed;
  }

  // -- Internals ----------------------------------------------------------------

  private _put(map: Map<string, CacheEntry>, id: string, entry: CacheEntry): void {
    this._drop(map, id);
    map.set(id, entry);
  }

  private _drop(map: Map<string, CacheEntry>, id: string): void {
    const existing = map.get(id);
    if (existing?.owned) existing.texture.destroy(true);
    map.delete(id);
  }
}

export interface PrewarmSpinTexturesOptions {
  /** The shared cache to fill. */
  cache: SpinTextureCache;
  /** Every symbolId to bake. */
  ids: string[];
  /**
   * Factory for a scratch symbol used as the render source. Typically the
   * same factory you registered (e.g. `() => new SpineReelSymbol(opts)`).
   * It is activated per id, snapshotted, and destroyed at the end.
   */
  createSymbol: () => ReelSymbol;
  /** Cell width the reels will use. */
  width: number;
  /** Cell height the reels will use. */
  height: number;
  /** Also bake the motion-blur variants. Default: true. */
  blurred?: boolean;
  /** Motion-blur tuning override (defaults to the cache's). */
  blur?: MotionBlurOptions;
}

/**
 * Bake spin textures for every symbolId up front (at load or between
 * rounds) so the first spin never pays a capture hitch. Ids that already
 * have a user-provided or captured texture are skipped by the cache.
 *
 * ```ts
 * prewarmSpinTextures({
 *   cache,
 *   ids: ['cherry', 'lemon', 'seven'],
 *   createSymbol: () => new SpineReelSymbol(spineOpts),
 *   width: 150,
 *   height: 150,
 * });
 * ```
 */
export function prewarmSpinTextures(options: PrewarmSpinTexturesOptions): void {
  const { cache, ids, width, height } = options;
  const blurred = options.blurred ?? true;
  const symbol = options.createSymbol();
  try {
    for (const id of ids) {
      if (symbol.symbolId !== id) symbol.activate(id);
      symbol.resize(width, height);
      cache.captureStatic(id, symbol.view, width, height);
      if (blurred) cache.captureBlurred(id, width, height, options.blur);
    }
  } finally {
    symbol.destroy();
  }
}
